<template>
  <Menubar
    :model="_navItems"
    class="min-h-[8vh]"
  >
    <!-- Menu hamburger à gauche -->
    <template
      v-if="HasToggleableMenu"
      #start
    >
      <button
        ref="hamburgerMenuRef"
        aria-label="Menu"
        class="p-1 mr-5 -ml-1 text-gray-500 rounded-md md:block"
        @click="toggleSidebar"
      >
        <svg
          class="w-6 h-6"
          aria-hidden="true"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            clip-rule="evenodd"
            fill-rule="evenodd"
            d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
          />
        </svg>
      </button>
    </template>

    <!-- Menu à droite -->
    <template #end>
      <div class="sm:flex sm:items-center sm:ml-2">
        <div
          v-if="user"
          class="mr-5"
        >
          <button
            type="button"
            class="p-1 rounded-md border border-transparent text-[var(--ocv-secondary-700)] transition hover:border-[var(--ocv-secondary-500)] hover:bg-[var(--ocv-secondary-50)] focus:outline-none focus:ring-2 focus:ring-[var(--ocv-secondary-500)]"
            aria-label="Personnaliser les couleurs"
            title="Personnaliser les couleurs"
            @click="openThemeColorsDialog"
          >
            <i
              class="pi pi-palette text-xl"
              aria-hidden="true"
            />
          </button>
        </div>

        <!-- Utilisateur connecté -->
        <div
          v-if="user"
          class="relative"
        >
          <button
            v-if="user.icon"
            type="button"
            class="flex text-sm rounded-full border-2 border-transparent transition focus:border-gray-300 focus:outline-none"
            aria-haspopup="true"
            aria-controls="overlay_menu"
            @click="toggle"
          >
            <Avatar
              class="mr-2"
              icon="pi pi-user"
              shape="circle"
              size="xlarge"
            />
          </button>

          <span
            v-else
            class="inline-flex rounded-md"
          >
            <button
              type="button"
              class="inline-flex items-center py-2 px-3 text-sm font-medium leading-4 text-gray-500 rounded-md border transition dark:text-gray-300 focus:outline-none hover:light:text-gray-700"
              aria-haspopup="true"
              aria-controls="overlay_menu"
              @click="toggle"
            >
              {{ user.name }}
              <svg
                class="ml-2 -mr-0.5 w-4 h-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>
          </span>
          <Menu
            id="overlay_menu"
            ref="menu"
            :model="profileItems"
            :popup="true"
          />
        </div>
        <!-- Utilisateur non connecté -->
        <div
          v-else
          class="relative"
        >
          <NuxtLink to="/login">
            <Button
              type="button"
              icon="pi pi-sign-in"
              label="Se connecter"
              :link="true"
            />
          </NuxtLink>
        </div>
      </div>
    </template>
  </Menubar>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useSidebarStore } from "~/stores/sidebar";
import type { MenuItem } from "primevue/menuitem";
import Menu from "primevue/menu";
import Button from "primevue/button";
import Avatar from "primevue/avatar";
import { useDialog } from "primevue/usedialog";
import { COOKIE_KEYS, type UserStoreCookie } from "~/constants/cookies";
import { getAvailableSpaces, updateSelectedSpace } from "~/utils/spaceManagement";
import UserThemeColorsForm from "~/components/user/ThemeColorsForm.vue";

useHead({
  link: [{ rel: "icon", href: "data:," }],
});

const { navItems = [], HasToggleableMenu } = defineProps<{
  navItems?: MenuItem[];
  HasToggleableMenu?: boolean;
}>();

const hamburgerMenuRef = ref<HTMLElement | null>(null);
defineExpose({ hamburgerMenuRef });

const { toggleSidebar } = useSidebarStore();

const authService = useAuthService();
const userStore = useUserStore();
const userStoreCookie = useCookie<UserStoreCookie>(COOKIE_KEYS.USER_STORE);
const user = await userStore.getUser();

const menu = ref();
const dialog = useDialog();

const _navItems = ref<MenuItem[]>(navItems || []);
const profileItems = ref<MenuItem[]>([]);

onMounted(() => {
  if (user.value) {
    profileItems.value = [
      {
        label: "Mon compte",
        items: [
          {
            label: "Mon profil",
            icon: "pi pi-user",
            command: async () => {
              await navigateTo("/city/profile");
            },
          },
          {
            label: "Se déconnecter",
            command: async () => {
              await authService.logout();
              await authService.resetAuthCookie();
              await authService.resetConnectedUserData();
              await navigateTo("/login");
            },
          },
        ],
      },
    ];
    const availableSpaces = getAvailableSpaces(user.value?.roles ?? []);
    if (availableSpaces.length > 1) {
      profileItems.value[0].items?.splice(1, 0, {
        label: "Changer d’espace",
        icon: "pi pi-th-large",
        command: async () => {
          updateSelectedSpace(userStore, userStoreCookie, null);
          await navigateTo("/choice-space");
        },
      });
    }
  }
});

const toggle = (event: Event) => menu.value?.toggle(event);
const openThemeColorsDialog = () => {
  dialog.open(UserThemeColorsForm, {
    props: {
      header: "Personnaliser les couleurs",
      modal: true,
      dismissableMask: true,
      draggable: false,
      style: {
        width: "min(94vw, 64rem)",
        margin: "0.75rem",
      },
      breakpoints: {
        "960px": "94vw",
        "640px": "98vw",
      },
    },
  });
};
</script>
